import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import {
    buildInactiveReasonConfirmationPayload,
    getClientTypeLabel,
    INACTIVE_REASON_EXPLANATION_MAX_LENGTH,
} from "../../helpers/clientActivityTransition";

const ClientInactivityConfirmationModal = ({
    open,
    transition,
    onConfirm,
    errorMessage,
}) => {
    const [answer, setAnswer] = useState(null);
    const [explanation, setExplanation] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const yesButtonRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setAnswer(null);
        setExplanation("");
        setIsSubmitting(false);
        submittingRef.current = false;
    }, [open, transition]);

    if (!transition) return null;

    const clientTypeLabel = getClientTypeLabel(transition.clientType);
    const explanationIsValid = explanation.trim().length > 0;
    const canConfirm =
        !isSubmitting &&
        (answer === true || (answer === false && explanationIsValid));

    const submit = async (event) => {
        event.preventDefault();
        if (!canConfirm || submittingRef.current) return;

        submittingRef.current = true;
        setIsSubmitting(true);
        try {
            await onConfirm(
                buildInactiveReasonConfirmationPayload(
                    transition,
                    answer,
                    explanation,
                ),
            );
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-[400]"
                initialFocus={yesButtonRef}
                onClose={() => {}}
                static
                open={open}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-full p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-2 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-2 sm:scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg p-6 overflow-hidden text-left bg-white shadow-2xl rounded-xl sm:p-7">
                                <Dialog.Title className="text-xl font-semibold text-slate-900">
                                    Is this {clientTypeLabel} still a client?
                                </Dialog.Title>
                                <Dialog.Description className="mt-2 text-sm leading-6 text-slate-600"></Dialog.Description>

                                <form onSubmit={submit} className="mt-6">
                                    <fieldset>
                                        <legend className="sr-only">
                                            Select whether the client is still a
                                            client
                                        </legend>
                                        <div className="grid grid-cols-2 gap-3">
                                            {[true, false].map((value) => {
                                                const selected = answer === value;
                                                const label = value ? "Yes" : "No";
                                                return (
                                                    <button
                                                        key={label}
                                                        ref={
                                                            value
                                                                ? yesButtonRef
                                                                : undefined
                                                        }
                                                        type="button"
                                                        role="radio"
                                                        aria-checked={selected}
                                                        disabled={isSubmitting}
                                                        onClick={() =>
                                                            setAnswer(value)
                                                        }
                                                        className={`rounded-lg border px-4 py-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                                                            selected
                                                                ? "border-teal-600 bg-teal-50 text-teal-800"
                                                                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                                                        }`}
                                                    >
                                                        {label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </fieldset>

                                    {answer === false ? (
                                        <div className="mt-5">
                                            <label
                                                htmlFor="inactive-reason-explanation"
                                                className="block text-sm font-medium text-slate-800"
                                            >
                                                Explain why this {clientTypeLabel} is
                                                no longer a client:
                                            </label>
                                            <textarea
                                                id="inactive-reason-explanation"
                                                value={explanation}
                                                onChange={(event) =>
                                                    setExplanation(
                                                        event.target.value,
                                                    )
                                                }
                                                maxLength={
                                                    INACTIVE_REASON_EXPLANATION_MAX_LENGTH
                                                }
                                                required
                                                rows={4}
                                                disabled={isSubmitting}
                                                placeholder="Briefly explain why the client discontinued services."
                                                className="block w-full px-3 py-2 mt-2 text-sm border rounded-lg shadow-sm border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:bg-slate-100"
                                            />
                                            <p className="mt-1 text-xs text-right text-slate-500">
                                                {explanation.length}/
                                                {
                                                    INACTIVE_REASON_EXPLANATION_MAX_LENGTH
                                                }
                                            </p>
                                        </div>
                                    ) : null}

                                    {errorMessage ? (
                                        <div
                                            role="alert"
                                            className="px-4 py-3 mt-4 text-sm text-red-700 rounded-lg bg-red-50"
                                        >
                                            {errorMessage}
                                        </div>
                                    ) : null}

                                    <button
                                        type="submit"
                                        disabled={!canConfirm}
                                        className="mt-6 inline-flex w-full justify-center rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
                                    >
                                        {isSubmitting ? "Saving..." : "Confirm"}
                                    </button>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default ClientInactivityConfirmationModal;
